import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  useEffect,
  useState,
} from 'react';

import { getCurrentUser, loginRequest, logoutRequest } from '@/api/apiAuth';
import type { LoginCredentials } from '@/api/apiTypes';
import { clearAuthTokens, getAuthTokens, setAuthTokens, subscribeToAuthChanges } from '@/auth/tokenStorage';
import { AuthContext, type AuthContextValue } from '@/providers/authContext';

const AUTH_QUERY_KEY = ['auth', 'current-user'] as const;

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [authVersion, setAuthVersion] = useState(0);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const hasStoredSession = Boolean(getAuthTokens());

  useEffect(
    () => subscribeToAuthChanges(() => {
      setAuthVersion((currentVersion) => currentVersion + 1);
    }),
    [],
  );

  const currentUserQuery = useQuery({
    queryKey: [...AUTH_QUERY_KEY, authVersion],
    queryFn: getCurrentUser,
    enabled: hasStoredSession,
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!hasStoredSession) {
      void queryClient.cancelQueries({ queryKey: AUTH_QUERY_KEY });
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
    }
  }, [hasStoredSession, queryClient]);

  const login = async (credentials: LoginCredentials) => {
    setIsLoggingIn(true);

    try {
      const tokens = await loginRequest(credentials);
      setAuthTokens(tokens);
      await queryClient.prefetchQuery({
        queryKey: [...AUTH_QUERY_KEY, authVersion + 1],
        queryFn: getCurrentUser,
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      if (hasStoredSession) {
        await logoutRequest();
      }
    } catch {
      // Backend logout is optional for JWT sessions; always clear the client session.
    } finally {
      clearAuthTokens();
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
    }
  };

  const value: AuthContextValue = {
    user: currentUserQuery.data ?? null,
    isAuthenticated: hasStoredSession && Boolean(currentUserQuery.data),
    isLoading: hasStoredSession && !currentUserQuery.data && currentUserQuery.isPending,
    isLoggingIn,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}